import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import VarietyModal from '../components/VarietyModal';
import CategoryFilterBar from '../components/CategoryFilterBar';
import PosProductCard from '../components/PosProductCard';
import ProductGrid from '../components/ProductGrid';
import { getProducts } from '../api';
import { normalizePaginatedResponse } from '../utils/pagination';
import useProductEvents from '../hooks/useProductEvents';
import { applyProductEvent } from '../utils/productEventPatch';
import { areAllPricesSoldOut } from '../utils/productAvailability';

const normalizeCategoryKey = (product) => {
  const raw = product?.categoryId ?? product?.Category?.id ?? 'uncategorized';
  return String(raw || 'uncategorized');
};

const resolveCategoryLabel = (product) => {
  return (
    product?.Category?.name ||
    product?.categoryName ||
    product?.categoryLabel ||
    (product?.categoryId ? `Categoria ${product.categoryId}` : 'Sin categoria')
  );
};

const getPricedPrices = (variety) =>
  (variety.VarietyPrices || []).filter((price) => price.price !== null && price.price !== undefined);

const hasCatalogPrice = (product) =>
  (product.Varieties || []).some((variety) => getPricedPrices(variety).length > 0);

const isProductSoldOut = (product) =>
  areAllPricesSoldOut((product.Varieties || []).flatMap(getPricedPrices));

const normalizeText = (value) => String(value || '').trim().toLowerCase();

const matchesSearch = (product, search) => {
  const normalizedSearch = search.trim().toLowerCase();
  if (!normalizedSearch) return true;
  const productName = normalizeText(product.productName);
  if (
    productName.includes(normalizedSearch) ||
    productName.split(' ').some((word) => word.startsWith(normalizedSearch))
  ) {
    return true;
  }
  return (product.Varieties || []).some((variety) =>
    normalizeText(variety.varietyName).includes(normalizedSearch)
  );
};

const compareProductsForSearch = (search) => (a, b) => {
  const normalizedSearch = search.trim().toLowerCase();
  const nameA = normalizeText(a.productName);
  const nameB = normalizeText(b.productName);
  if (normalizedSearch) {
    const aExact = nameA === normalizedSearch;
    const bExact = nameB === normalizedSearch;
    if (aExact !== bExact) return aExact ? -1 : 1;
    const aStarts = nameA.startsWith(normalizedSearch);
    const bStarts = nameB.startsWith(normalizedSearch);
    if (aStarts !== bStarts) return aStarts ? -1 : 1;
    if (aStarts && bStarts && nameA.length !== nameB.length) return nameA.length - nameB.length;
  }
  return a.productName.localeCompare(b.productName);
};

const buildCategoryOptions = (productsList) => {
  const options = new Map();
  productsList.forEach((product) => {
    const id = normalizeCategoryKey(product);
    const label = resolveCategoryLabel(product);
    const existing = options.get(id);
    if (!existing) {
      options.set(id, {
        id,
        label,
        initial: String(label || id).trim().charAt(0).toUpperCase(),
      });
      return;
    }
    if (label && existing.label !== label) {
      options.set(id, {
        ...existing,
        label,
        initial: String(label).trim().charAt(0).toUpperCase(),
      });
    }
  });
  return Array.from(options.values()).sort((a, b) => a.label.localeCompare(b.label));
};

const ProductGallery = () => {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const branchId = useParams().branchId || localStorage.getItem('selectedBranchId');

  const buildFilteredProducts = (productsList, search, category) => {
    return productsList.filter((product) => {
      if (!hasCatalogPrice(product)) return false;
      if (category !== 'all' && normalizeCategoryKey(product) !== String(category)) return false;
      return matchesSearch(product, search);
    });
  };

  const refreshProducts = useCallback(async () => {
    try {
      const data = await getProducts(branchId || 1, { pageSize: 0 });
      const normalized = normalizePaginatedResponse(data);
      const productsData = normalized.data || [];
      setProducts(productsData);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  }, [branchId]);

  const handleProductEvent = useCallback((event) => {
    const nextProducts = applyProductEvent(products, event);
    if (!nextProducts) {
      refreshProducts();
      return;
    }
    if (nextProducts === products) return;
    setProducts(nextProducts);
  }, [products, refreshProducts]);

  useProductEvents(branchId, handleProductEvent);

  useEffect(() => {
    refreshProducts();
  }, [refreshProducts]);

  useEffect(() => {
    setFilteredProducts(buildFilteredProducts(products, searchTerm, selectedCategory));
  }, [products, searchTerm, selectedCategory]);

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);
    filterProducts(value, selectedCategory);
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    filterProducts(searchTerm, category);
  };

  const filterProducts = (search, category) => {
    setFilteredProducts(buildFilteredProducts(products, search, category));
  };

  return (
    <>
      <div className="p-6 bg-gray-100 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col gap-3 mb-6 sm:flex-row sm:items-center sm:justify-between">
            <h1 className="text-2xl font-bold text-gray-900">Lista de Productos</h1>
            <Link
              to="/"
              className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700"
            >
              Solicitar cotización
            </Link>
          </div>

          {/* Search Bar */}
          <div className="sticky top-0 pt-2 z-10 bg-gray-100">
            <input
              type="text"
              placeholder="Buscar producto..."
              value={searchTerm}
              onChange={(e) => {
                handleSearch(e);
              }}
              className="w-full mb-6 p-3 border border-gray-300 rounded-lg shadow focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <CategoryFilterBar
            categories={buildCategoryOptions(products.filter(hasCatalogPrice))}
            selectedCategory={selectedCategory}
            onSelect={handleCategorySelect}
            includeAll
            allItem={{ id: 'all', label: 'Todos', initial: 'T' }}
            itemsWrapperClassName="mb-6"
          />

          {/* Product Grid */}
          <div className="space-y-8">
            {(selectedCategory === 'all'
              ? buildCategoryOptions(filteredProducts)
              : buildCategoryOptions(filteredProducts).filter((category) => String(category.id) === String(selectedCategory))
            ).map((category) => {
              const productsInCategory = filteredProducts
                .filter((product) => normalizeCategoryKey(product) === String(category.id))
                .sort(compareProductsForSearch(searchTerm));

              if (productsInCategory.length === 0) return null;

              return (
                <div key={category.id}>
                  {/* Category Title */}
                  <div className="flex items-center gap-4 my-6">
                    <div className="flex-grow border-t border-gray-300"></div>
                    <h2 className="text-lg font-bold text-gray-700 whitespace-nowrap">
                      {category.label}
                    </h2>
                    <div className="flex-grow border-t border-gray-300"></div>
                  </div>

                  {/* Products Grid */}
                  <ProductGrid>
                    {productsInCategory.map((product) => (
                      <PosProductCard
                        key={product.id}
                        productName={product.productName}
                        imageSrc={product.productImage || 'https://placehold.co/200'}
                        badge={isProductSoldOut(product) ? 'Agotado' : ''}
                        onClick={() => setSelectedProduct(product)}
                      />
                    ))}
                  </ProductGrid>
                </div>
              );
            })}
            {filteredProducts.length === 0 && (
              <div className="rounded-lg bg-white p-6 text-center text-sm text-gray-500 shadow">
                No hay productos para los filtros seleccionados.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal for Varieties */}
      {selectedProduct && (
        <VarietyModal product={selectedProduct} onClose={() => setSelectedProduct(null)} />
      )}
    </>
  );
};

export default ProductGallery;
